// Intake.AI Settings Page - Manage public intake link and availability
import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  Scale,
  ChevronLeft,
  Link as LinkIcon,
  Copy,
  ExternalLink,
  Calendar,
  Clock,
  Settings,
  Globe,
  CheckCircle,
  Loader2,
  Save,
  XCircle,
  Mail,
  Unlink,
  AlertTriangle,
  RefreshCw,
  Zap,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const TIME_OPTIONS = [];
for (let h = 6; h <= 21; h++) {
  for (let m = 0; m < 60; m += 30) {
    const hour = h.toString().padStart(2, '0');
    const minute = m.toString().padStart(2, '0');
    const period = h < 12 ? 'AM' : 'PM';
    const displayHour = h <= 12 ? h : h - 12;
    TIME_OPTIONS.push({
      value: `${hour}:${minute}`,
      label: `${displayHour === 0 ? 12 : displayHour}:${minute} ${period}`
    });
  }
}

export default function IntakeSettingsPage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    public_intake_enabled: false,
    intake_slug: '',
    firm_display_name: '',
    scheduling_enabled: false,
    appointment_duration: 30,
    buffer_time: 15,
    max_advance_days: 30,
    availability: {
      monday: { enabled: true, start_time: '09:00', end_time: '17:00' },
      tuesday: { enabled: true, start_time: '09:00', end_time: '17:00' },
      wednesday: { enabled: true, start_time: '09:00', end_time: '17:00' },
      thursday: { enabled: true, start_time: '09:00', end_time: '17:00' },
      friday: { enabled: true, start_time: '09:00', end_time: '17:00' },
      saturday: { enabled: false, start_time: '09:00', end_time: '12:00' },
      sunday: { enabled: false, start_time: '09:00', end_time: '12:00' }
    },
    calendar_provider: null,
    calendar_connected: false,
    calendar_email: null
  });
  const [slugAvailable, setSlugAvailable] = useState(null);
  const [checkingSlug, setCheckingSlug] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [connectingCalendar, setConnectingCalendar] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const [testingCalendar, setTestingCalendar] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchCalendarStatus();
    
    // Handle OAuth callback params
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const message = searchParams.get('message');
    
    if (success === 'microsoft_connected') {
      toast.success('Microsoft Calendar connected successfully!');
      fetchCalendarStatus();
    } else if (success === 'google_connected') {
      toast.success('Google Calendar connected successfully!');
      fetchCalendarStatus();
    } else if (error) {
      toast.error(`Calendar connection failed: ${message || error}`);
    }
  }, [token, searchParams]);

  const fetchSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/intake/settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/oauth/calendar/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCalendarStatus(data);
      }
    } catch (error) {
      console.error('Error fetching calendar status:', error);
    }
  };

  const connectCalendar = async (provider) => {
    setConnectingCalendar(provider);
    try {
      const res = await fetch(`${API_URL}/api/oauth/${provider}/authorize`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        // Redirect to OAuth provider
        window.location.href = data.auth_url;
      } else {
        const error = await res.json();
        toast.error(error.detail || `Failed to connect ${provider} calendar`);
        setConnectingCalendar(null);
      }
    } catch (error) {
      toast.error(`Failed to connect ${provider} calendar`);
      setConnectingCalendar(null);
    }
  };

  const disconnectCalendar = async () => {
    setDisconnecting(true);
    try {
      const res = await fetch(`${API_URL}/api/oauth/calendar/disconnect`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success('Calendar disconnected successfully');
        setCalendarStatus({ connected: false, provider: null, email: null });
        setTestResult(null);
        // Also update local settings
        setSettings(prev => ({
          ...prev,
          calendar_connected: false,
          calendar_provider: null,
          calendar_email: null
        }));
      } else {
        toast.error('Failed to disconnect calendar');
      }
    } catch (error) {
      toast.error('Failed to disconnect calendar');
    } finally {
      setDisconnecting(false);
    }
  };

  const testCalendarConnection = async () => {
    setTestingCalendar(true);
    setTestResult(null);
    try {
      const res = await fetch(`${API_URL}/api/oauth/calendar/test`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setTestResult(data);
      
      if (data.success) {
        toast.success(`Calendar test passed! Found ${data.events_today || 0} events today.`);
      } else {
        toast.error('Calendar test failed. See recommendations below.');
      }
    } catch (error) {
      setTestResult({
        success: false,
        error: 'Failed to test calendar connection',
        recommendations: [{
          title: 'Network Error',
          description: 'Unable to reach the server. Please check your internet connection.',
          steps: ['Check your internet connection', 'Refresh the page and try again'],
          priority: 'high'
        }]
      });
      toast.error('Failed to test calendar connection');
    } finally {
      setTestingCalendar(false);
    }
  };

  const checkSlugAvailability = async (slug) => {
    if (!slug || slug.length < 3) {
      setSlugAvailable(null);
      return;
    }
    
    setCheckingSlug(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/settings/check-slug/${slug}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setSlugAvailable(data.available);
        // Update slug to cleaned version
        setSettings(prev => ({ ...prev, intake_slug: data.slug }));
      }
    } catch (error) {
      console.error('Error checking slug:', error);
    } finally {
      setCheckingSlug(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateDayAvailability = (day, field, value) => {
    setSettings(prev => ({
      ...prev,
      availability: {
        ...prev.availability,
        [day]: {
          ...prev.availability[day],
          [field]: value
        }
      }
    }));
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/intake/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(settings)
      });

      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        toast.success('Settings saved successfully');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const copyLink = () => {
    const url = `${window.location.origin}/intake/${settings.intake_slug}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard');
  };

  const publicIntakeUrl = settings.intake_slug 
    ? `${window.location.origin}/intake/${settings.intake_slug}`
    : null;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-center">
          <Settings className="w-12 h-12 text-emerald-600 mx-auto mb-4" />
          <p className="text-muted-foreground">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="intake-settings-page">
      {/* Header */}
      <header className="sticky top-0 z-50 w-full border-b bg-white/95 backdrop-blur">
        <div className="flex h-16 items-center px-4 lg:px-8">
          <Link to="/intake" className="flex items-center gap-2 mr-8">
            <Button variant="ghost" size="sm">
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Pipeline
            </Button>
          </Link>
          
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold">Intake Settings</h1>
            <p className="text-sm text-muted-foreground">Manage your public intake link and scheduling</p>
          </div>

          <Button onClick={saveSettings} disabled={saving}>
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Save Changes
          </Button>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto py-8 px-4">
        <Tabs defaultValue="public-link" className="space-y-6">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="public-link" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Public Link
            </TabsTrigger>
            <TabsTrigger value="scheduling" className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Scheduling
            </TabsTrigger>
            <TabsTrigger value="calendar" className="flex items-center gap-2">
              <LinkIcon className="w-4 h-4" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="notifications" className="flex items-center gap-2">
              <Mail className="w-4 h-4" />
              Notifications
            </TabsTrigger>
          </TabsList>

          {/* Public Link Tab */}
          <TabsContent value="public-link">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5 text-emerald-600" />
                  Public Intake Link
                </CardTitle>
                <CardDescription>
                  Create a permanent link that you can add to your website. Prospective clients can use this link to fill out intake forms and schedule consultations.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Public Intake */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Public Intake</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow anyone with the link to submit intake forms
                    </p>
                  </div>
                  <Switch
                    checked={settings.public_intake_enabled}
                    onCheckedChange={(checked) => updateSetting('public_intake_enabled', checked)}
                  />
                </div>

                {/* Firm Display Name */}
                <div className="space-y-2">
                  <Label htmlFor="firm_name">Firm Name (shown to clients)</Label>
                  <Input
                    id="firm_name"
                    value={settings.firm_display_name || ''}
                    onChange={(e) => updateSetting('firm_display_name', e.target.value)}
                    placeholder="Smith & Associates Law Firm"
                  />
                </div>

                {/* Custom Slug */}
                <div className="space-y-2">
                  <Label htmlFor="slug">Custom URL Slug</Label>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">
                        /intake/
                      </span>
                      <Input
                        id="slug"
                        className="pl-16"
                        value={settings.intake_slug || ''}
                        onChange={(e) => {
                          updateSetting('intake_slug', e.target.value);
                          checkSlugAvailability(e.target.value);
                        }}
                        placeholder="your-firm-name"
                      />
                    </div>
                    {checkingSlug && <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />}
                    {slugAvailable === true && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                    {slugAvailable === false && <span className="text-sm text-red-600">Taken</span>}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Use letters, numbers, and hyphens. This will be your permanent intake URL.
                  </p>
                </div>

                {/* Preview Link */}
                {settings.public_intake_enabled && publicIntakeUrl && (
                  <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <Label className="text-emerald-800 mb-2 block">Your Public Intake Link</Label>
                    <div className="flex items-center gap-2">
                      <Input 
                        value={publicIntakeUrl} 
                        readOnly 
                        className="font-mono text-sm bg-white"
                      />
                      <Button size="sm" variant="outline" onClick={copyLink}>
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => window.open(publicIntakeUrl, '_blank')}
                      >
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-emerald-700 mt-2">
                      Add this link to your website or share it directly with prospective clients.
                    </p>
                  </div>
                )}

                {/* Form Customization Link */}
                <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h4 className="font-semibold text-purple-800">Customize Your Intake Form</h4>
                      <p className="text-sm text-purple-700 mt-1">
                        Add your logo, customize colors, manage case types, add custom questions, and more.
                      </p>
                    </div>
                    <Button
                      onClick={() => navigate('/intake/customize')}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Customize Form
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Scheduling Tab */}
          <TabsContent value="scheduling">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-emerald-600" />
                  Appointment Scheduling
                </CardTitle>
                <CardDescription>
                  Set your availability for client consultations. Clients can select from available time slots when submitting their intake form.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Scheduling */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Scheduling</Label>
                    <p className="text-sm text-muted-foreground">
                      Allow clients to schedule consultation appointments
                    </p>
                  </div>
                  <Switch
                    checked={settings.scheduling_enabled}
                    onCheckedChange={(checked) => updateSetting('scheduling_enabled', checked)}
                  />
                </div>

                {settings.scheduling_enabled && (
                  <>
                    {/* Appointment Settings */}
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label>Appointment Duration</Label>
                        <Select 
                          value={settings.appointment_duration.toString()}
                          onValueChange={(v) => updateSetting('appointment_duration', parseInt(v))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">15 minutes</SelectItem>
                            <SelectItem value="30">30 minutes</SelectItem>
                            <SelectItem value="45">45 minutes</SelectItem>
                            <SelectItem value="60">60 minutes</SelectItem>
                            <SelectItem value="90">90 minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Buffer Time</Label>
                        <Select 
                          value={settings.buffer_time.toString()}
                          onValueChange={(v) => updateSetting('buffer_time', parseInt(v))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">No buffer</SelectItem>
                            <SelectItem value="5">5 minutes</SelectItem>
                            <SelectItem value="10">10 minutes</SelectItem>
                            <SelectItem value="15">15 minutes</SelectItem>
                            <SelectItem value="30">30 minutes</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Max Advance Booking</Label>
                        <Select 
                          value={settings.max_advance_days.toString()}
                          onValueChange={(v) => updateSetting('max_advance_days', parseInt(v))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">1 week</SelectItem>
                            <SelectItem value="14">2 weeks</SelectItem>
                            <SelectItem value="30">1 month</SelectItem>
                            <SelectItem value="60">2 months</SelectItem>
                            <SelectItem value="90">3 months</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Weekly Availability */}
                    <div className="space-y-4">
                      <Label className="text-base font-medium">Weekly Availability</Label>
                      <div className="space-y-3">
                        {DAYS_OF_WEEK.map((day) => (
                          <div 
                            key={day.key} 
                            className={`flex items-center gap-4 p-3 rounded-lg border ${
                              settings.availability[day.key]?.enabled 
                                ? 'bg-white' 
                                : 'bg-slate-50'
                            }`}
                          >
                            <div className="w-28">
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={settings.availability[day.key]?.enabled || false}
                                  onCheckedChange={(checked) => 
                                    updateDayAvailability(day.key, 'enabled', checked)
                                  }
                                />
                                <span className={`font-medium ${
                                  settings.availability[day.key]?.enabled 
                                    ? 'text-foreground' 
                                    : 'text-muted-foreground'
                                }`}>
                                  {day.label}
                                </span>
                              </div>
                            </div>

                            {settings.availability[day.key]?.enabled && (
                              <div className="flex items-center gap-2 flex-1">
                                <Select
                                  value={settings.availability[day.key]?.start_time || '09:00'}
                                  onValueChange={(v) => 
                                    updateDayAvailability(day.key, 'start_time', v)
                                  }
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {TIME_OPTIONS.map((time) => (
                                      <SelectItem key={time.value} value={time.value}>
                                        {time.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>

                                <span className="text-muted-foreground">to</span>

                                <Select
                                  value={settings.availability[day.key]?.end_time || '17:00'}
                                  onValueChange={(v) => 
                                    updateDayAvailability(day.key, 'end_time', v)
                                  }
                                >
                                  <SelectTrigger className="w-32">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {TIME_OPTIONS.map((time) => (
                                      <SelectItem key={time.value} value={time.value}>
                                        {time.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            )}

                            {!settings.availability[day.key]?.enabled && (
                              <span className="text-sm text-muted-foreground">Unavailable</span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Calendar Integration Tab */}
          <TabsContent value="calendar">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <LinkIcon className="w-5 h-5 text-emerald-600" />
                  Calendar Integration
                </CardTitle>
                <CardDescription>
                  Connect your calendar for live availability sync. When connected, available time slots will be automatically determined based on your actual calendar.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Connected Status */}
                {calendarStatus?.connected ? (
                  <div className="space-y-4">
                    <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-emerald-600" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-emerald-800">
                              {calendarStatus.provider === 'microsoft' ? 'Microsoft Outlook' : 'Google Calendar'} Connected
                            </h3>
                            <p className="text-sm text-emerald-700 flex items-center gap-1">
                              <Mail className="w-4 h-4" />
                              {calendarStatus.email}
                            </p>
                            {calendarStatus.connected_at && (
                              <p className="text-xs text-emerald-600 mt-1">
                                Connected on {new Date(calendarStatus.connected_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            onClick={testCalendarConnection}
                            disabled={testingCalendar}
                            data-testid="test-calendar-btn"
                          >
                            {testingCalendar ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Zap className="w-4 h-4 mr-2" />
                            )}
                            Test Connection
                          </Button>
                          <Button
                            variant="outline"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            onClick={disconnectCalendar}
                            disabled={disconnecting}
                            data-testid="disconnect-calendar-btn"
                          >
                            {disconnecting ? (
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                              <Unlink className="w-4 h-4 mr-2" />
                            )}
                            Disconnect
                          </Button>
                        </div>
                      </div>
                      
                      <div className="mt-4 p-3 bg-white rounded-lg border border-emerald-200">
                        <p className="text-sm text-emerald-800">
                          <CheckCircle className="w-4 h-4 inline mr-1" />
                          Your calendar is now synced. Available time slots will be automatically calculated based on your real calendar availability.
                        </p>
                      </div>
                    </div>

                    {/* Test Results */}
                    {testResult && (
                      <div className={`p-4 rounded-lg border ${testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                        <div className="flex items-start gap-3">
                          {testResult.success ? (
                            <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                          ) : (
                            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                          )}
                          <div className="flex-1">
                            <h4 className={`font-semibold ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                              {testResult.success ? 'Connection Test Passed' : 'Connection Test Failed'}
                            </h4>
                            {testResult.success ? (
                              <div className="mt-2 text-sm text-green-700">
                                <p>{testResult.message}</p>
                                {testResult.test_details && (
                                  <ul className="mt-2 space-y-1">
                                    <li>✓ Token refresh: {testResult.test_details.token_refresh}</li>
                                    <li>✓ Calendar access: {testResult.test_details.calendar_access}</li>
                                    <li>✓ Events found today: {testResult.test_details.busy_times_fetched}</li>
                                  </ul>
                                )}
                              </div>
                            ) : (
                              <p className="mt-1 text-sm text-red-700">{testResult.error}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* AI Recommendations */}
                    {testResult && !testResult.success && testResult.recommendations && testResult.recommendations.length > 0 && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                        <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-3">
                          <Zap className="w-5 h-5" />
                          AI Recommended Solutions
                        </h4>
                        <div className="space-y-3">
                          {testResult.recommendations.map((rec, index) => (
                            <div key={index} className={`p-3 bg-white rounded-lg border ${
                              rec.priority === 'high' ? 'border-red-200' : 
                              rec.priority === 'medium' ? 'border-amber-200' : 'border-gray-200'
                            }`}>
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h5 className="font-medium text-gray-900 flex items-center gap-2">
                                    {rec.priority === 'high' && <span className="w-2 h-2 rounded-full bg-red-500" />}
                                    {rec.priority === 'medium' && <span className="w-2 h-2 rounded-full bg-amber-500" />}
                                    {rec.priority === 'low' && <span className="w-2 h-2 rounded-full bg-gray-400" />}
                                    {rec.title}
                                  </h5>
                                  <p className="text-sm text-gray-600 mt-1">{rec.description}</p>
                                  {rec.steps && rec.steps.length > 0 && (
                                    <ol className="mt-2 text-sm text-gray-700 list-decimal list-inside space-y-1">
                                      {rec.steps.map((step, stepIndex) => (
                                        <li key={stepIndex}>{step}</li>
                                      ))}
                                    </ol>
                                  )}
                                </div>
                                {rec.action === 'reconnect' && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={disconnectCalendar}
                                    className="ml-3"
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Reconnect
                                  </Button>
                                )}
                                {rec.action === 'external' && rec.link && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => window.open(rec.link, '_blank')}
                                    className="ml-3"
                                  >
                                    <ExternalLink className="w-3 h-3 mr-1" />
                                    Open
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <p className="text-muted-foreground">
                      Connect your calendar to automatically sync your availability with prospective clients.
                    </p>
                    
                    {/* Microsoft Connect */}
                    <div className="p-4 border rounded-lg hover:border-blue-300 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <svg viewBox="0 0 23 23" className="w-6 h-6">
                              <path fill="#f25022" d="M1 1h10v10H1z"/>
                              <path fill="#00a4ef" d="M1 12h10v10H1z"/>
                              <path fill="#7fba00" d="M12 1h10v10H12z"/>
                              <path fill="#ffb900" d="M12 12h10v10H12z"/>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-medium">Microsoft Outlook</h4>
                            <p className="text-sm text-muted-foreground">Connect your Microsoft 365 or Outlook.com calendar</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => connectCalendar('microsoft')}
                          disabled={connectingCalendar === 'microsoft'}
                          data-testid="connect-microsoft-btn"
                        >
                          {connectingCalendar === 'microsoft' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <LinkIcon className="w-4 h-4 mr-2" />
                          )}
                          Connect
                        </Button>
                      </div>
                    </div>
                    
                    {/* Google Connect */}
                    <div className="p-4 border rounded-lg hover:border-red-300 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-white border flex items-center justify-center">
                            <svg viewBox="0 0 24 24" className="w-6 h-6">
                              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                          </div>
                          <div>
                            <h4 className="font-medium">Google Calendar</h4>
                            <p className="text-sm text-muted-foreground">Connect your Google Workspace or personal calendar</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => connectCalendar('google')}
                          disabled={connectingCalendar === 'google'}
                          data-testid="connect-google-btn"
                        >
                          {connectingCalendar === 'google' ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <LinkIcon className="w-4 h-4 mr-2" />
                          )}
                          Connect
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
                  <ul className="text-sm text-blue-700 space-y-1">
                    <li>• Click "Connect" to sign in to your Microsoft or Google account</li>
                    <li>• Grant permission to read your calendar availability</li>
                    <li>• Available time slots will be synced automatically</li>
                    <li>• Booked appointments will appear in your calendar</li>
                  </ul>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Notifications Tab */}
          <TabsContent value="notifications">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-emerald-600" />
                  Stage Transition Notifications
                </CardTitle>
                <CardDescription>
                  Get notified when leads automatically move through your pipeline stages.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Enable Notifications */}
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <Label className="text-base font-medium">Enable Notifications</Label>
                    <p className="text-sm text-muted-foreground">
                      Receive email alerts when leads change stages
                    </p>
                  </div>
                  <Switch
                    checked={settings.notifications_enabled ?? true}
                    onCheckedChange={(checked) => setSettings({...settings, notifications_enabled: checked})}
                    data-testid="notifications-enabled-switch"
                  />
                </div>

                {/* Notification Email */}
                <div className="space-y-3">
                  <Label className="text-base font-medium">Notification Email</Label>
                  <p className="text-sm text-muted-foreground">
                    Enter the email address where you'd like to receive lead notifications. 
                    Leave blank to use your account email.
                  </p>
                  <Input
                    type="email"
                    placeholder="notifications@yourfirm.com"
                    value={settings.notification_email || ''}
                    onChange={(e) => setSettings({...settings, notification_email: e.target.value})}
                    data-testid="notification-email-input"
                    disabled={!settings.notifications_enabled}
                  />
                </div>

                {/* Per-Stage Notification Preferences */}
                <div className={`space-y-4 ${!settings.notifications_enabled ? 'opacity-50 pointer-events-none' : ''}`}>
                  <div>
                    <Label className="text-base font-medium">Stage Notifications</Label>
                    <p className="text-sm text-muted-foreground mb-4">
                      Choose which stage transitions you want to be notified about.
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {/* Consultation Scheduled */}
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="w-3 h-3 rounded-full bg-blue-500" />
                        <div>
                          <p className="font-medium text-sm">Consultation Scheduled</p>
                          <p className="text-xs text-muted-foreground">Client books an appointment</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.notification_stages?.consultation_scheduled ?? true}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          notification_stages: {
                            ...settings.notification_stages,
                            consultation_scheduled: checked
                          }
                        })}
                        data-testid="notify-consultation-scheduled"
                      />
                    </div>
                    
                    {/* Consultation Complete */}
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="w-3 h-3 rounded-full bg-green-500" />
                        <div>
                          <p className="font-medium text-sm">Consultation Complete</p>
                          <p className="text-xs text-muted-foreground">Appointment completed or AI analysis run</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.notification_stages?.consultation_complete ?? true}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          notification_stages: {
                            ...settings.notification_stages,
                            consultation_complete: checked
                          }
                        })}
                        data-testid="notify-consultation-complete"
                      />
                    </div>
                    
                    {/* Agreement Sent */}
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="w-3 h-3 rounded-full bg-purple-500" />
                        <div>
                          <p className="font-medium text-sm">Agreement Sent</p>
                          <p className="text-xs text-muted-foreground">Legal agreement sent for signature</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.notification_stages?.agreement_sent ?? true}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          notification_stages: {
                            ...settings.notification_stages,
                            agreement_sent: checked
                          }
                        })}
                        data-testid="notify-agreement-sent"
                      />
                    </div>
                    
                    {/* Client Signed */}
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors bg-emerald-50/50">
                      <div className="flex items-center gap-3">
                        <span className="w-3 h-3 rounded-full bg-emerald-500" />
                        <div>
                          <p className="font-medium text-sm">Client Signed! 🎉</p>
                          <p className="text-xs text-muted-foreground">Agreement signed, lead converted</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.notification_stages?.signed_converted ?? true}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          notification_stages: {
                            ...settings.notification_stages,
                            signed_converted: checked
                          }
                        })}
                        data-testid="notify-signed-converted"
                      />
                    </div>
                    
                    {/* Needs Follow-up */}
                    <div className="flex items-center justify-between p-3 border rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="w-3 h-3 rounded-full bg-amber-500" />
                        <div>
                          <p className="font-medium text-sm">Needs Follow-up</p>
                          <p className="text-xs text-muted-foreground">Appointment cancelled</p>
                        </div>
                      </div>
                      <Switch
                        checked={settings.notification_stages?.contacted ?? true}
                        onCheckedChange={(checked) => setSettings({
                          ...settings, 
                          notification_stages: {
                            ...settings.notification_stages,
                            contacted: checked
                          }
                        })}
                        data-testid="notify-contacted"
                      />
                    </div>
                  </div>
                </div>

                {/* Save Button */}
                <div className="flex justify-end pt-4">
                  <Button onClick={saveSettings} disabled={saving} data-testid="save-notifications-btn">
                    {saving ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Save Notification Settings
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
